/**
 * Shared constants and types across the pdf-ticket monorepo.
 *
 * Brand: pdf.ticket.jp (production), pdf-ticket.beta.menu (staging).
 * Codename / package scope: pdf-ticket / @pdf-ticket/*.
 */

export const BRAND = {
  name: 'pdf.ticket',
  domainProduction: 'pdf.ticket.jp',
  domainStaging: 'pdf-ticket.beta.menu',
  editorPath: '/edit',
  tagline: 'Cloudflare-native PDF editor — フォント耐性と差分のみ保存。',
} as const;

/**
 * The 5 differentiation pillars. Free plan covers ALL of these.
 * Workers AI features sit on top and require Pro.
 */
export const PILLARS = [
  { id: 'free', label: '無料', detail: 'コア機能はすべて無料。' },
  { id: 'easy', label: '使いやすい', detail: '入った瞬間に編集が始められる。' },
  { id: 'fast', label: '高速', detail: 'Cloudflareエッジで世界中どこからでも。' },
  { id: 'font-preservation', label: 'フォント耐性', detail: '元のフォントは絶対に書き換えない。' },
  { id: 'incremental-save', label: '差分のみ保存', detail: 'バイト単位で原本を保ったまま追記。' },
] as const;

export type PillarId = (typeof PILLARS)[number]['id'];

export type Plan = 'free' | 'pro';

/**
 * Feature identifiers. Anything tagged `ai:*` is Pro-only (Workers AI cost
 * burden stays with paying users).
 */
export type Feature =
  | 'core:edit-text'
  | 'core:add-image'
  | 'core:annotate'
  | 'core:rotate-page'
  | 'core:remove-page'
  | 'core:incremental-save'
  | 'share:public-link'
  | 'export:image'
  | 'ai:ocr'
  | 'ai:font-match'
  | 'ai:rewrite'
  | 'ai:summarize';

export interface QuotaLimit {
  readonly perDay: number | 'unlimited';
  readonly perMonth: number | 'unlimited';
  readonly maxFileBytes: number;
}

export interface QuotaPolicy {
  readonly plan: Plan;
  readonly limits: Readonly<Record<Feature, QuotaLimit>>;
}

export interface QuotaUsage {
  readonly feature: Feature;
  readonly usedToday: number;
  readonly usedThisMonth: number;
  /** ISO8601 UTC, e.g. "2026-06-01T00:00:00Z". Local quotas reset at this moment. */
  readonly resetAt: string;
}

export interface QuotaCheckResult {
  readonly allowed: boolean;
  readonly reason:
    | 'within-limits'
    | 'plan-locked'
    | 'daily-cap'
    | 'monthly-cap'
    | 'file-too-large';
  readonly upgrade: Plan | null;
}
